import { differenceInMinutes, format, isSameDay, isSameYear } from 'date-fns'

import {
  describeContent,
  isFailedSendState,
} from '../ConversationCard/conversationDisplay'
import type { ThreadMessageData } from '../fragments/threadMessage'

/**
 * When a message happened, as the thread orders and labels it. A message we
 * sent that the network has not published yet has no `publishedAt`; it keeps
 * its place by the time we handed it over.
 */
export function getMessageTime(
  message: Pick<ThreadMessageData, 'publishedAt' | 'send'>,
): Date | null {
  const iso = message.publishedAt ?? message.send?.sentAt ?? null
  return iso ? new Date(iso) : null
}

/** Messages within this gap of each other read as one exchange. */
const GROUP_GAP_MINUTES = 60

export type MessageGroup<TMessage> = {
  /** The centred line above the group; null when the group has no time. */
  dateLabel: string | null
  messages: TMessage[]
}

/**
 * Splits the thread into groups, each headed by one centred date line. A new
 * group starts when the day changes or more than an hour has passed since the
 * previous message, so a quick back-and-forth reads as one exchange and a
 * reply the next morning gets its own timestamp.
 */
export function groupMessages<
  TMessage extends Pick<ThreadMessageData, 'publishedAt' | 'send'>,
>(
  messages: ReadonlyArray<TMessage>,
  now: Date = new Date(),
): MessageGroup<TMessage>[] {
  const groups: MessageGroup<TMessage>[] = []
  let previousTime: Date | null = null

  for (const message of messages) {
    const time = getMessageTime(message)
    const startsGroup =
      groups.length === 0 ||
      time === null ||
      previousTime === null ||
      !isSameDay(time, previousTime) ||
      differenceInMinutes(time, previousTime) > GROUP_GAP_MINUTES

    if (startsGroup) {
      groups.push({
        dateLabel: time ? formatDateLine(time, now) : null,
        messages: [message],
      })
    } else {
      groups[groups.length - 1].messages.push(message)
    }

    previousTime = time ?? previousTime
  }

  return groups
}

/**
 * The date line's text: just the time for today, day and month otherwise, and
 * the year only once the message is from another year.
 */
export function formatDateLine(date: Date, now: Date = new Date()): string {
  if (isSameDay(date, now)) return format(date, 'h:mm aaa')
  if (isSameYear(date, now)) return format(date, "d MMM 'at' h:mm aaa")
  return format(date, "d MMM yyyy 'at' h:mm aaa")
}

/**
 * Where the "New" separator sits: the index of the first message the reader
 * has not seen, or `null` when there is nothing new. Only inbound messages
 * count as unread; our own replies are never new to us.
 */
export function findFirstUnreadIndex(
  messages: ReadonlyArray<
    Pick<ThreadMessageData, 'isOwnMessage' | 'publishedAt' | 'send'>
  >,
  lastReadAt: string | null,
): number | null {
  const lastRead = lastReadAt ? new Date(lastReadAt) : null
  const index = messages.findIndex((message) => {
    if (message.isOwnMessage) return false
    const time = getMessageTime(message)
    return lastRead === null || (time !== null && time > lastRead)
  })
  return index === -1 ? null : index
}

/**
 * The avatar marks the end of a run of inbound bubbles, so it shows on an
 * inbound message when the next one is ours or there is no next one.
 */
export function showsAvatar(
  messages: ReadonlyArray<Pick<ThreadMessageData, 'isOwnMessage'>>,
  index: number,
): boolean {
  const message = messages[index]
  if (!message || message.isOwnMessage) return false
  const next = messages[index + 1]
  return next === undefined || next.isOwnMessage
}

/**
 * Whether the network rejected a message we sent. Only our own messages carry
 * a send; a rejected one keeps its bubble and gains a "Message not sent" line.
 */
export function isFailedSend(
  message: Pick<ThreadMessageData, 'isOwnMessage' | 'send'>,
): boolean {
  return message.isOwnMessage && isFailedSendState(message.send?.state)
}

export type BubbleContent =
  | { kind: 'text'; text: string }
  | {
      kind: 'image'
      text: string | null
      url: string
      width: number | null
      height: number | null
    }
  | { kind: 'placeholder'; text: string }

/**
 * What goes inside the bubble. An unsent message and content the thread
 * cannot show yet both become a single placeholder line; an image renders
 * when the network still serves its URL, with any caption above it.
 */
export function getBubbleContent(
  message: Pick<
    ThreadMessageData,
    'text' | 'contentType' | 'assets' | 'isServiceDeleted'
  >,
): BubbleContent {
  if (message.isServiceDeleted) {
    return { kind: 'placeholder', text: 'This message was unsent' }
  }

  const image = message.assets.find(
    (asset) => asset.type === 'image' && asset.url,
  )
  if (image?.url) {
    return {
      kind: 'image',
      text: message.text || null,
      url: image.url,
      width: image.width ?? null,
      height: image.height ?? null,
    }
  }

  if (message.text) {
    return { kind: 'text', text: message.text }
  }

  return {
    kind: 'placeholder',
    text: describeContent(message.contentType) || 'Sent a message',
  }
}
